import { useState, useEffect } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { TripParticipantsDialog } from "@/components/TripParticipantsDialog";
import { AppHeader } from "@/components/header/AppHeader";
import { TripManager } from "@/components/TripManager";
import { NewTripDialog } from "@/components/trip/NewTripDialog";
import { TripContent } from "@/components/trip/TripContent";
import { useToast } from "@/hooks/use-toast";
import { useTripManagement } from "@/hooks/useTripManagement";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FeatureCard } from "@/components/ui/feature-card";
import { StatBadge } from "@/components/ui/stat-badge";
import { 
  Plane, Map, Compass, Sun, Cloud, Wind,
  Globe2, Wallet, Users2, Camera, Mountain,
  Clock, Shield, Sparkles, History, Calendar, ArrowRight, Tag
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { TripInsights } from "@/components/TripInsights";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WeatherWidget } from "@/components/travel/WeatherWidget";
import { CurrencyConverter } from "@/components/travel/CurrencyConverter";
import { TravelTips } from "@/components/travel/TravelTips";

const Index = () => {
  const { toast } = useToast();
  const {
    currentTripName,
    tripHistory,
    expenses,
    handleAddExpense,
    handleDeleteExpense,
    handleStartNewTrip,
    handleEndTrip
  } = useTripManagement();

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const [showParticipantsDialog, setShowParticipantsDialog] = useState(false);
  const [tripParticipants, setTripParticipants] = useState<string[]>(() => {
    const saved = localStorage.getItem('tripParticipants');
    return saved ? JSON.parse(saved) : [];
  });

  // Get the tab parameter from URL
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab');

  useEffect(() => {
    // If tab=history is present in URL, switch to history tab
    if (activeTab === 'history') {
      const historyTab = document.querySelector('[data-tab="history"]');
      if (historyTab instanceof HTMLElement) {
        historyTab.click();
      }
    }
  }, [activeTab]);

  const handleSaveParticipants = (participants: string[]) => {
    setTripParticipants(participants);
    toast({
      title: "Participants Saved",
      description: `${participants.length} participants added to the trip.`,
    });
  };

  const handleExportCSV = () => {
    toast({
      title: "Export Successful",
      description: "Your expenses have been exported to CSV format.",
    });
  };

  const handleViewAllTrips = () => {
    const historyTab = document.querySelector('[data-tab="history"]');
    if (historyTab instanceof HTMLElement) {
      historyTab.click();
    }
  };

  const features = [
    {
      icon: Globe2,
      title: "Global Coverage",
      description: "Track expenses in multiple currencies",
      color: "text-blue-500",
      bg: "bg-blue-100"
    },
    {
      icon: Wallet,
      title: "Smart Budgeting",
      description: "Set and monitor trip budgets easily",
      color: "text-green-500",
      bg: "bg-green-100"
    },
    {
      icon: Users2,
      title: "Group Expenses",
      description: "Split costs among travel companions",
      color: "text-purple-500",
      bg: "bg-purple-100"
    },
    {
      icon: Camera,
      title: "Trip Memories",
      description: "Capture and organize travel photos",
      color: "text-pink-500",
      bg: "bg-pink-100"
    }
  ];

  const destinations = [
    {
      title: "Beach Paradise",
      image: "linear-gradient(225deg, #FFE29F 0%, #FFA99F 48%, #FF719A 100%)",
      rating: "4.8",
      category: "Beaches"
    },
    {
      title: "Mountain Adventure",
      image: "linear-gradient(90deg, hsla(221, 45%, 73%, 1) 0%, hsla(220, 78%, 29%, 1) 100%)",
      rating: "4.9",
      category: "Mountains"
    },
    {
      title: "City Explorer",
      image: "linear-gradient(109.6deg, rgba(223,234,247,1) 11.2%, rgba(244,248,252,1) 91.1%)",
      rating: "4.7",
      category: "Cities"
    }
  ];

  const renderTripHistoryCard = (trip: any) => {
    const startDate = new Date(trip.startDate);
    const endDate = new Date(trip.endDate);
    const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    return (
      <Card
        key={trip.startDate}
        className="p-6 hover:shadow-lg transition-all duration-300 group cursor-pointer bg-gradient-to-br from-white to-primary/5"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-xl bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              {trip.name}
            </h3>
            <Badge variant="outline" className="bg-primary/10 font-semibold">
              ₹{trip.totalAmount.toFixed(2)}
            </Badge>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Duration
              </p>
              <p className="font-medium">{duration} days</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Users2 className="h-4 w-4" />
                Participants
              </p>
              <p className="font-medium">{trip.stats?.totalParticipants || 0}</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Tag className="h-4 w-4" />
              Categories
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(trip.stats?.categoryBreakdown || {}).map(([category, amount]: [string, any]) => (
                <Badge key={category} variant="secondary" className="flex items-center gap-1">
                  {category}
                  <span className="ml-1 text-xs">₹{Number(amount).toFixed(2)}</span>
                </Badge>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {new Date(trip.startDate).toLocaleDateString()} - {new Date(trip.endDate).toLocaleDateString()}
              </span>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:text-primary/90"
                  >
                    View Details
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                      <Map className="h-5 w-5 text-primary" />
                      {trip.name}
                    </DialogTitle>
                    <DialogDescription>
                      Trip details and insights from {new Date(trip.startDate).toLocaleDateString()} to {new Date(trip.endDate).toLocaleDateString()}
                    </DialogDescription>
                  </DialogHeader>
                  <ScrollArea className="h-[60vh]">
                    <div className="space-y-6 p-4">
                      <TripInsights expenses={trip.expenses} />
                    </div>
                  </ScrollArea>
                  <DialogFooter>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        Close
                      </Button>
                    </DialogTrigger>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-1 md:py-8 relative overflow-hidden">
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-primary origin-left z-50"
        style={{ scaleX }}
      />
      
      <motion.div 
        className="absolute top-0 right-0 w-96 h-96 bg-gradient-secondary opacity-10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <motion.div 
        className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-accent opacity-10 rounded-full blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [90, 0, 90],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      
      <div className="container mx-auto px-2 md:px-4 flex flex-col min-h-screen max-w-full relative z-10">
        <AppHeader 
          onOpenParticipants={() => setShowParticipantsDialog(true)}
          onExportCSV={handleExportCSV}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex-grow space-y-6"
        >
          {!currentTripName ? (
            <div className="space-y-8">
              {/* Hero Section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center space-y-4"
              >
                <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                  Track Your Travel Expenses with Ease
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Start your journey with TravelPocket - your smart travel companion for expense tracking and budget management.
                </p>
                <NewTripDialog onStartNewTrip={handleStartNewTrip} />
              </motion.div>

              {/* Features Grid */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
              >
                {features.map((feature) => (
                  <FeatureCard
                    key={feature.title}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    iconColor={feature.color}
                    bgColor={feature.bg}
                  />
                ))}
              </motion.div>

              {/* Travel Widgets */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <WeatherWidget />
                <CurrencyConverter />
                <TravelTips />
              </motion.div>

              {/* Trip History Preview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold flex items-center gap-2">
                    <History className="h-6 w-6 text-primary" />
                    Recent Trips
                  </h2>
                  <Badge variant="secondary" className="animate-pulse">
                    <Clock className="h-4 w-4 mr-1" />
                    Trip History
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tripHistory.length > 0 ? (
                    tripHistory.slice(-3).map((trip) => renderTripHistoryCard(trip))
                  ) : (
                    <Card className="col-span-full p-8 text-center space-y-4 bg-gradient-to-br from-gray-50 to-gray-100">
                      <History className="h-12 w-12 text-primary mx-auto opacity-50" />
                      <h3 className="text-xl font-semibold">No Trip History Yet</h3>
                      <p className="text-muted-foreground">
                        Your completed trips will appear here after you end them.
                      </p>
                    </Card>
                  )}
                </div>
                
                {tripHistory.length > 3 && (
                  <div className="text-center">
                    <Button
                      variant="outline"
                      className="group"
                      onClick={handleViewAllTrips}
                    >
                      View All Trips
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                )}
              </motion.div>

              {/* Popular Destinations */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold">Popular Destinations</h2>
                  <Badge variant="secondary" className="animate-pulse">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Trending
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {destinations.map((destination, index) => (
                    <Card
                      key={destination.title}
                      className="overflow-hidden group cursor-pointer"
                    >
                      <div 
                        className="h-48 w-full bg-cover bg-center"
                        style={{ background: destination.image }}
                      >
                        <div className="h-full w-full bg-black bg-opacity-30 p-6 flex flex-col justify-between text-white">
                          <div className="flex justify-between items-start">
                            <Badge className="bg-white/20 backdrop-blur-sm">
                              {destination.category}
                            </Badge>
                            <Badge variant="secondary">
                              ★ {destination.rating}
                            </Badge>
                          </div>
                          <h3 className="text-xl font-semibold group-hover:scale-105 transition-transform">
                            {destination.title}
                          </h3>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </motion.div>

              {/* Quick Stats */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4"
              >
                <Card className="p-4 text-center hover:shadow-md transition-shadow">
                  <Clock className="h-8 w-8 mx-auto text-primary mb-2" />
                  <p className="text-sm text-gray-600">Real-time Tracking</p>
                </Card>
                <Card className="p-4 text-center hover:shadow-md transition-shadow">
                  <Shield className="h-8 w-8 mx-auto text-secondary mb-2" />
                  <p className="text-sm text-gray-600">Secure Data</p>
                </Card>
                <Card className="p-4 text-center hover:shadow-md transition-shadow">
                  <Globe2 className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                  <p className="text-sm text-gray-600">Multiple Currencies</p>
                </Card>
                <Card className="p-4 text-center hover:shadow-md transition-shadow">
                  <Mountain className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  <p className="text-sm text-gray-600">Travel Insights</p>
                </Card>
              </motion.div>
            </div>
          ) : (
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 md:mb-6"
              >
                <TripManager 
                  expenses={expenses}
                  tripName={currentTripName}
                  onEndTrip={handleEndTrip}
                />
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <WeatherWidget />
                <CurrencyConverter />
                <TravelTips />
              </div>

              <TripContent 
                tripParticipants={tripParticipants}
                onAddExpense={handleAddExpense}
                onDeleteExpense={handleDeleteExpense}
              />
            </div>
          )}
        </motion.div>

        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-2 md:mt-8 py-2 md:py-4 text-center text-xs md:text-sm text-gray-600 bg-gradient-glass backdrop-blur-sm rounded-lg"
        >
          <p className="font-medium">
            travelpocket developed by mohammed maaz
          </p>
        </motion.footer>
      </div>

      <TripParticipantsDialog
        open={showParticipantsDialog}
        onOpenChange={setShowParticipantsDialog}
        onSave={handleSaveParticipants}
        initialParticipants={tripParticipants}
      />
    </div>
  );
};

export default Index;